'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from '@workspace/interfaces';
import { getActivePathFromLink, isLinkActive } from '@workspace/lib';
import { INavigationGroup, INavigationItem } from '@workspace/interfaces/header';
import { isNavigationGroup } from '@workspace/lib/header.utils';

interface UseNavigationGroupParams {
  group: INavigationGroup;
  activePath?: string;
  onActivePathChange?: (newPath: string) => void;
}

export const useNavigationGroup = (params: UseNavigationGroupParams) => {
  const { group, activePath, onActivePathChange } = params;

  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Element;
    if (!target.closest('.mega-menu-container') && !target.closest('button')) {
      setIsMegaMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!isMegaMenuOpen) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMegaMenuOpen, handleClickOutside]);

  const handleNavigationClick = useCallback(
    (link?: Link) => {
      if (!link || !onActivePathChange) return;
      if (link.isOpenInNewTab || link.type === 'external') return;
      const newPath = getActivePathFromLink(link);
      if (newPath) onActivePathChange(newPath);
    },
    [onActivePathChange],
  );

  const handleGroupClick = useCallback(() => {
    if (group.link) {
      if (group.link.isOpenInNewTab || group.link.type === 'external') return;
      handleNavigationClick(group.link);
    }
  }, [group.link, handleNavigationClick]);

  const isMegaMenu = useMemo(
    () => group.groupTitle === 'Products' || group.groupTitle === 'Solutions',
    [group.groupTitle],
  );

  const handleButtonClick = useCallback(() => {
    if (isMegaMenu) {
      setIsMegaMenuOpen((prev) => !prev);
    } else {
      if (group.link && (group.link.isOpenInNewTab || group.link.type === 'external')) {
        return;
      }
      handleGroupClick();
    }
  }, [isMegaMenu, group.link, handleGroupClick]);

  const isActive = useMemo(() => (group.link ? isLinkActive(group.link, activePath) : false), [group.link, activePath]);

  const { items, navigationGroups, navigationItems } = useMemo(() => {
    const groupItems = group.items || [];
    const groups = groupItems.filter((item) => isNavigationGroup(item)) as INavigationGroup[];
    const navItems = groupItems.filter((item): item is INavigationItem => !isNavigationGroup(item));
    return {
      items: groupItems,
      navigationGroups: groups,
      navigationItems: navItems,
    };
  }, [group.items]);

  return {
    isMegaMenuOpen,
    setIsMegaMenuOpen,
    handleNavigationClick,
    handleGroupClick,
    handleButtonClick,
    isActive,
    isMegaMenu,
    navigationGroups,
    navigationItems,
    items,
  };
};
